import { makeStyles, styled, ThemeProvider } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import React, { useEffect, useMemo } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { formatDateByLocale } from '../../../utils/index.jsx';
import { formatSelectedDatesAsRanges } from '../../../utils/appointmentDateRanges';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import moment from 'moment';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { Snackbar, Typography } from '@mui/material';
import { Alert } from '@mui/lab';
import { useSnackBarState } from '../../../hooks';

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme { }
}

interface AppointmentDatePickerProps extends LocalizeContextProps {
    availableDaysWeek: string[];
    blockedDates: number[],
    slotsPerDay: number,
    autoCurrentDate?: boolean,
    selectBlockedDates?: boolean,
    // SHER-615: agenda browsing must allow past dates; appointment create/edit must not (SHER-440).
    allowPastDates?: boolean,
    multiSelect?: boolean,
    initialDate?: Date,
    onDateChangeCallback: (date: Date) => void;
    onDatesChangeCallback?: (dates: Date[]) => void;
    datesOccupancy: { [date: string]: number }
}


const useStyles = makeStyles((theme) => ({
    dayPicker: {
        color: "#000000de",
        backgroundColor: "transparent",
        //backgroundColor: theme.palette.primary.main,
        fontWeight: 500,
        transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        tapHighlightColor: 'transparent',
        outline: 0,
        border: 0,
        padding: 0,
        cursor: 'pointer',
        userSelect: 'none',
        verticalAlign: 'middle',
        textDecoration: 'none',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: '0.75rem',
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        margin: '0 2px',
    },
    dayWithDotContainer: {
        position: 'relative',
    },
    dayWithDot: {
        position: 'absolute',
        height: 0,
        width: 0,
        border: '4px solid',
        borderRadius: 4,
        right: '50%',
        transform: 'translateX(4px)',
        top: '75%'
    },
    freeDay: {
        borderColor: 'green',
        backgroundColor: "green",
        "&:hover": {
            backgroundColor: "green",
        }
    },
    halfBusyDay: {
        borderColor: 'orange',
        backgroundColor: "orange",
        "&:hover": {
            backgroundColor: "orange",
        }
    },
    veryBusyDay: {
        borderColor: 'red',
        backgroundColor: "red",
        "&:hover": {
            backgroundColor: "red",
        }
    },
    fullDay: {
        borderColor: 'black',
        backgroundColor: "black",
        "&:hover": {
            backgroundColor: "black",
        }
    },
    blockedDay: {
        borderColor: 'black',
        backgroundColor: "black",
        "&:hover": {
            backgroundColor: "black",
        }
    },
    daySelected: {
        color: "#ffffff",
        fontWeight: 500,
        width: '36px',
        height: '36px',
    },
    daySelectedMulti: {
        outline: '2px solid #ffffff',
        outlineOffset: '1px',
    },
    dayDisabled: {
        color: "grey",
        cursor: 'not-allowed',
    }
}))



const AppointmentDatePicker: React.FC<AppointmentDatePickerProps> = ({ availableDaysWeek, autoCurrentDate, blockedDates,
                                                                        slotsPerDay, activeLanguage, datesOccupancy,
                                                                        initialDate, multiSelect, allowPastDates,
                                                                        selectBlockedDates, onDateChangeCallback, onDatesChangeCallback, translate }) => {
    const nextAvailableDate = useMemo(() =>{
        return findNextAvailableDate();
    },[]);
    
    // Ensure date initialization is valid with proper fallbacks
    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(() => {
        try {
            if (initialDate && dayjs(initialDate).isValid()) {
                return dayjs(initialDate).startOf('day');
            } else if (autoCurrentDate && nextAvailableDate && nextAvailableDate.isValid()) {
                return nextAvailableDate.startOf('day'); 
            }
            return null;
        } catch (error) {
            console.error("Error initializing date:", error);
            return null;
        }
    });
    const [selectedDates, setSelectedDates] = React.useState<string[]>([]);
    const [dateError, setDateError] = React.useState<string | null>(null);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const classes = useStyles();

    function occupancyLevel(date: Dayjs) {
        if(date){
            const momentObj = moment(date.toDate().getTime());
            const dateString = momentObj.format('YYYY-MM-DD');
            
            if (datesOccupancy.hasOwnProperty(dateString)) {
                if (slotsPerDay === datesOccupancy[dateString]) {
                    return classes.fullDay;
                }
                else {
                    const percent = datesOccupancy[dateString] / slotsPerDay;
                    return percent < 0.3 ? classes.freeDay : percent < 0.6 ? classes.halfBusyDay : classes.veryBusyDay;
                }
            }
            else {
                return classes.freeDay;
            }
        }
        
    }

    function isBlockedDate(date: Dayjs | Date): boolean {
        const dateObj = date instanceof Date ? date : date.toDate();
        return blockedDates.some((blockedTS) => {
            const blockedDate = new Date(blockedTS);
            return blockedDate.toDateString() === dateObj.toDateString();
        });
    }

    function isPastDate(date: Dayjs | Date): boolean {
        const candidate = dayjs(date instanceof Date ? date : date.toDate()).startOf('day');
        return candidate.isBefore(dayjs().startOf('day'));
    }

    // Single guard point for the SHER-440 past-date policy: every guard site must
    // go through this so allowPastDates (SHER-615) is never forgotten.
    function isForbiddenPastDate(date: Dayjs | Date): boolean {
        return !allowPastDates && isPastDate(date);
    }

    function isDisabledDate(date: Dayjs | Date, selectBlockedDates?: boolean) {
        const dateObj = date instanceof Date ? date : date.toDate();

        if (isForbiddenPastDate(dateObj)) {
            return true;
        }

        const currentWeekDay = dateObj.toLocaleDateString("en-En", { weekday: 'short' });
        const isAnOfficeDay = availableDaysWeek.includes(currentWeekDay);
        
        // Blocked dates should always be disabled (not selectable for appointments)
        // Even in edit mode, blocked dates can't be selected for appointments
        const isABlockedDate = isBlockedDate(dateObj);
        
        // If it's not an office day, it's disabled
        if (!isAnOfficeDay) {
            return true;
        }
        
        // Blocked dates are always disabled from selection
        if (isABlockedDate) {
            return true;
        }
        
        return false;
    }

    function dateKey(date: Dayjs | Date): string {
        const dateObj = date instanceof Date ? date : date.toDate();
        return moment(dateObj.getTime()).format('YYYY-MM-DD');
    }

    function onAcceptDate() {
        try {
            setDateError(null);
            if (multiSelect) {
                if (selectedDates.length === 0) {
                    setDateError(translate("pages.hospital.outpatients.no_date_selected").toString());
                    return;
                }
                const dates = selectedDates
                    .map((key) => dayjs(key).toDate())
                    .sort((a, b) => a.getTime() - b.getTime());
                if (onDatesChangeCallback) {
                    onDatesChangeCallback(dates);
                }
                if (dates.length === 1) {
                    onDateChangeCallback(dates[0]);
                }
                return;
            }

            if (!selectedDate) {
                setDateError(translate("pages.hospital.outpatients.no_date_selected").toString());
                return;
            }
            
            // Make sure the date is valid before proceeding
            if (!selectedDate.isValid()) {
                setDateError(translate("pages.hospital.outpatients.invalid_date").toString());
                return;
            }
            
            // Check if the date is blocked - prevent selection
            if (isBlockedDate(selectedDate)) {
                setShowSnackbar({
                    show: true,
                    message: "pages.hospital.outpatients.date_blocked",
                    severity: "warning"
                });
                return; // Don't call the callback, prevent accepting blocked date
            }
            
            // Check if the date is disabled
            if (isDisabledDate(selectedDate, selectBlockedDates)) {
                setDateError(translate("pages.hospital.outpatients.date_not_available").toString());
                return;
            }
            
            onDateChangeCallback(selectedDate.toDate());
        } catch (error) {
            console.error("Error processing selected date:", error);
            setDateError(translate("pages.hospital.outpatients.date_error").toString());
        }
    }
    function findNextAvailableDate() {
        for (let i = 0; i < 60; i++) {
            const nextAvailableDate = dayjs().add(i, 'day');
            
            if (!isDisabledDate(nextAvailableDate.toDate(), selectBlockedDates)) {
                return nextAvailableDate;
            }
        }
        return dayjs();
    }
    useEffect(() => {
        if (!multiSelect || !onDatesChangeCallback) {
            return;
        }
        const dates = selectedDates
            .map((key) => dayjs(key).toDate())
            .sort((a, b) => a.getTime() - b.getTime());
        onDatesChangeCallback(dates);
    }, [selectedDates, multiSelect]);

    useEffect(() => {
        if (autoCurrentDate && selectedDate && !multiSelect) {
            onDateChangeCallback(selectedDate?.toDate());
            return;
        }
    }, [])

    const CustomDay = (props: PickersDayProps<Dayjs>) => {
        const { day, outsideCurrentMonth, onDaySelect, ...other } = props;
        const selected = multiSelect
            ? selectedDates.includes(dateKey(day))
            : day.toDate().getTime() === selectedDate?.toDate().getTime();
        const isToday = dayjs().startOf('day').isSame(day.startOf('day'));
        const isBlocked = isBlockedDate(day);
        
        const occupancyClass = occupancyLevel(day);
        // For blocked dates, use blockedDay class instead of occupancy class
        const dotClass = isBlocked ? classes.blockedDay : occupancyClass;
        let classesString = classes.dayPicker;
        classesString = selected ? classesString + " " + classes.daySelected + " " + occupancyClass : classesString;
        classesString = selected && multiSelect ? classesString + " " + classes.daySelectedMulti : classesString;
        classesString = isToday ? classesString + " MuiPickersDay-today" : classesString;
        
        const handleDayClick = (date: Dayjs) => {
            if (isDisabledDate(date, selectBlockedDates)) {
                return;
            }
            if (multiSelect) {
                const key = dateKey(date);
                setSelectedDates((current) => (
                    current.includes(key)
                        ? current.filter((value) => value !== key)
                        : [...current, key].sort()
                ));
                return;
            }
            setSelectedDate(date);
            if (onDaySelect) {
                onDaySelect(date);
            }
        };

        const isDisabled = isDisabledDate(day, selectBlockedDates) || outsideCurrentMonth;

        if(isDisabled){
            return (
                <button 
                    className={`${classes.dayPicker} ${classes.dayDisabled}`} 
                    type="button"
                    disabled={true}>
                    { day.format('DD') }
                    {isBlocked && !outsideCurrentMonth && (
                        <div className={`${classes.dayWithDot} ${classes.blockedDay}`}/>
                    )}
                </button>
            );
        }
        
        return (
            <button 
                className={classesString} 
                onClick={() => handleDayClick(day)} 
                type="button">
                { day.format('DD') }
                <div className={`${classes.dayWithDot} ${dotClass}`}/>
            </button>
        );        
    }

    // Handle date changes safely
    const handleDateChange = (newDate: Dayjs | null | undefined) => {
        try {
            setDateError(null);
            if (!newDate) {
                setSelectedDate(null);
                return;
            }
            // Validate the new date
            if (!newDate.isValid()) {
                setDateError(translate("pages.hospital.outpatients.invalid_date").toString());
                return;
            }

            if (isForbiddenPastDate(newDate)) {
                setDateError(translate("pages.hospital.outpatients.date_not_available").toString());
                return;
            }
            
            // Check if the selected date is blocked and show warning
            if (isBlockedDate(newDate)) {
                setShowSnackbar({
                    show: true,
                    message: "pages.hospital.outpatients.date_blocked",
                    severity: "warning"
                });
            }
            
            setSelectedDate(newDate);
        } catch (error) {
            console.error("Error handling date change:", error);
            setDateError(translate("pages.hospital.outpatients.date_error").toString());
        }
    };

    const selectedDatesRangeLabel = useMemo(
        () => formatSelectedDatesAsRanges(selectedDates, activeLanguage.code),
        [selectedDates, activeLanguage.code],
    );

    return (
        <>
            <MobileDatePicker
                value={multiSelect ? null : (selectedDate ?? null)}
                onChange={handleDateChange}
                onAccept={onAcceptDate}
                label={translate("pages.hospital.outpatients.select_date").toString()}
                minDate={allowPastDates ? undefined : dayjs().startOf('day')}
                shouldDisableDate={(date) => isDisabledDate(date, selectBlockedDates)}
                format={formatDateByLocale(activeLanguage.code)}
                slots={{
                    day: CustomDay as any,
                }}
                slotProps={{
                    day: {
                        selected: false,
                    } as any,
                    ...(multiSelect ? {
                        toolbar: {
                            toolbarPlaceholder: selectedDatesRangeLabel || '––',
                        },
                    } : {}),
                }}
            />
            {multiSelect && selectedDatesRangeLabel &&
                <Typography variant="body2" sx={{ mt: 1 }}>{selectedDatesRangeLabel}</Typography>
            }
            {dateError &&
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>{dateError}</Typography>
            }
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}>
                <div>
                    {
                        (showSnackbar.message && showSnackbar.severity) &&
                        <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>
        </>
    );
};

export default withLocalize(AppointmentDatePicker);
